/**
 * PracticePulseMenu — The living, breathing org-chart command center.
 *
 * A wide glass-morphism mega-panel that drops from the top nav when the user
 * clicks the "Team" button.  Two halves:
 *   • LEFT (55%) — an animated SVG org graph: admin at top, supervisors in a
 *     middle band with pulse-connections streaming out to their supervisees,
 *     each clinician wrapped in an Apple-Watch-style caseload orbit ring.
 *     Nodes color-shift green→amber→red as their caseload nears cap.  Click
 *     a supervisor to spring-zoom the graph.  Click any node to filter the
 *     Assignment Board and jump to it.
 *   • RIGHT (45%) — an adaptive action rail: three live stat cards with
 *     mini-sparklines, three role-gated pill buttons, and an AI Suggestion
 *     card that recommends a caseload rebalance if the practice is imbalanced.
 *
 * Keyboard: `T` opens (bound at the parent), `Esc` closes, `1/2/3` jump to
 * the three sub-pages.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  X, Crown, Users as UsersIcon, UserCog, ClipboardList, TrendingUp,
  AlertTriangle, Sparkles, ChevronRight, Zap, Activity, Send,
} from 'lucide-react';
import rbac from '../utils/rbac';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Health color helper — green (0-79%), amber (80-99%), rose (100%+)
const utilizationColor = (used, cap) => {
  if (!cap) return { fill: '#10b981', ring: 'stroke-emerald-400', label: 'text-emerald-600' }; // no cap set
  const pct = used / cap;
  if (pct >= 1) return { fill: '#f43f5e', ring: 'stroke-rose-400', label: 'text-rose-600' };
  if (pct >= 0.8) return { fill: '#f59e0b', ring: 'stroke-amber-400', label: 'text-amber-600' };
  return { fill: '#10b981', ring: 'stroke-emerald-400', label: 'text-emerald-600' };
};

// Build supervisor → supervisees mapping + admin list from users/clients arrays.
const buildOrgTree = (users = [], clients = []) => {
  const admins = users.filter(u => rbac.isAdmin(u));
  const supervisors = users.filter(u => u.is_supervisor || u.role === 'supervisor' || u.role === 'clinical_supervisor');
  const clinicians = users.filter(u => !rbac.isAdmin(u) && !u.is_supervisor && u.role !== 'supervisor' && u.role !== 'clinical_supervisor');

  // Count clients each user is assigned to (any role in assignments[])
  const caseloadCounts = new Map();
  (clients || []).forEach(c => {
    if (c.is_active === false) return;
    (c.assignments || []).forEach(a => {
      if (!a.therapist_id) return;
      caseloadCounts.set(a.therapist_id, (caseloadCounts.get(a.therapist_id) || 0) + 1);
    });
    if (c.assigned_therapist_id && !(c.assignments || []).some(a => a.therapist_id === c.assigned_therapist_id)) {
      caseloadCounts.set(c.assigned_therapist_id, (caseloadCounts.get(c.assigned_therapist_id) || 0) + 1);
    }
  });

  // Group clinicians under their supervisor (or "Unassigned" bucket)
  const bySupervisor = new Map();
  supervisors.forEach(s => bySupervisor.set(s.id, []));
  const unassigned = [];
  clinicians.forEach(cl => {
    const supId = cl.supervisor_id;
    if (supId && bySupervisor.has(supId)) {
      bySupervisor.get(supId).push(cl);
    } else {
      unassigned.push(cl);
    }
  });

  return { admins, supervisors, clinicians, bySupervisor, unassigned, caseloadCounts };
};

// ─── Node component (a single staff member circle w/ orbit ring) ──────────
const OrbitNode = ({ user, x, y, r = 22, used = 0, cap = 12, onClick, focused, dimmed }) => {
  const pct = cap ? Math.min(used / cap, 1) : 0;
  const c = utilizationColor(used, cap);
  const initials = (user.full_name || user.email || '?')
    .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const ringR = r + 5;
  const circumference = 2 * Math.PI * ringR;
  const dash = pct * circumference;

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: dimmed ? 0.25 : 1, scale: focused ? 1.18 : 1 }}
      transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      data-testid={`pulse-node-${user.id}`}
    >
      {/* orbit-ring track */}
      <circle cx={x} cy={y} r={ringR} fill="none" strokeWidth="3" className="stroke-slate-700/40" />
      {/* orbit-ring fill (arc = utilization) */}
      <circle
        cx={x} cy={y} r={ringR} fill="none" strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference - dash}`}
        transform={`rotate(-90 ${x} ${y})`}
        className={c.ring}
        style={{ filter: 'drop-shadow(0 0 4px currentColor)' }}
      />
      {/* main circle */}
      <circle cx={x} cy={y} r={r} fill={c.fill} fillOpacity="0.25" stroke={c.fill} strokeWidth="1.5" />
      {/* initials */}
      <text x={x} y={y + 4} textAnchor="middle" className="fill-white font-semibold text-[11px]">{initials}</text>
      {/* utilization label below */}
      {cap > 0 && (
        <text x={x} y={y + r + 14} textAnchor="middle" className={`${c.label} font-medium text-[9px]`}>
          {used}/{cap}
        </text>
      )}
    </motion.g>
  );
};

// ─── The big animated graph ────────────────────────────────────────────────
const OrgGraph = ({ tree, focusedSupId, setFocusedSupId, onNodeClick, hoverUser, setHoverUser }) => {
  const { admins, supervisors, bySupervisor, unassigned, caseloadCounts } = tree;
  const W = 720;
  const H = 500;
  const cx = W / 2;

  // Admin cluster (single row up top)
  const adminY = 55;
  const adminSpacing = Math.min(70, (W - 100) / Math.max(admins.length, 1));
  const adminXs = admins.map((_, i) => cx + (i - (admins.length - 1) / 2) * adminSpacing);

  // Supervisor band (mid-Y)
  const supY = 200;
  const supCount = Math.max(supervisors.length, 1);
  const supSpacing = Math.min(180, (W - 120) / supCount);
  const supXs = supervisors.map((_, i) => cx + (i - (supCount - 1) / 2) * supSpacing);

  // Unaffiliated / no-supervisor clinicians — rendered in a bottom band
  // so they're always visible on the graph.  Wraps 6 per row.
  const unaffBandTop = 380;
  const unaffCols = 8;
  const unaffXStep = (W - 80) / unaffCols;
  const unaffRows = Math.ceil(unassigned.length / unaffCols);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + Math.max(0, unaffRows - 1) * 55}`} className="select-none" style={{ maxHeight: 560 }}>
      <defs>
        {/* animated shimmer along connection lines */}
        <linearGradient id="pulseLine" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
          <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
          <animate attributeName="x1" from="-1" to="1" dur="3s" repeatCount="indefinite" />
          <animate attributeName="x2" from="0" to="2" dur="3s" repeatCount="indefinite" />
        </linearGradient>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Connection lines: admins → supervisors */}
      {admins.map((a, ai) => supervisors.map((s, si) => (
        <line
          key={`a${ai}-s${si}`}
          x1={adminXs[ai]} y1={adminY + 24}
          x2={supXs[si]} y2={supY - 24}
          stroke="url(#pulseLine)"
          strokeWidth="1.5"
        />
      )))}

      {/* Supervisor → supervisees connection lines */}
      {supervisors.map((s, si) => {
        const supervisees = bySupervisor.get(s.id) || [];
        return supervisees.map((cl, ci) => {
          const cliRow = Math.floor(ci / 4); // wrap every 4
          const cliCol = ci % 4;
          const cliX = supXs[si] + (cliCol - 1.5) * 48;
          const cliY = 320 + cliRow * 72;
          const isDimmed = focusedSupId && focusedSupId !== s.id;
          return (
            <line
              key={`s${si}-c${ci}`}
              x1={supXs[si]} y1={supY + 24}
              x2={cliX} y2={cliY - 22}
              stroke="url(#pulseLine)"
              strokeWidth="1.5"
              opacity={isDimmed ? 0.15 : 0.85}
            />
          );
        });
      })}

      {/* Admin nodes */}
      {admins.map((a, i) => (
        <motion.g key={a.id}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
          onMouseEnter={() => setHoverUser(a)}
          onMouseLeave={() => setHoverUser(null)}
        >
          <circle cx={adminXs[i]} cy={adminY} r={24} fill="#a855f7" fillOpacity="0.3" stroke="#a855f7" strokeWidth="1.5" filter="url(#glow)" />
          <Crown x={adminXs[i] - 8} y={adminY - 16} className="fill-purple-300" width={16} height={16} />
          <text x={adminXs[i]} y={adminY + 6} textAnchor="middle" className="fill-white font-bold text-[11px]">
            {(a.full_name || 'Admin').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
          </text>
          <text x={adminXs[i]} y={adminY + 40} textAnchor="middle" className="fill-slate-300 text-[9px] font-medium">
            {(a.full_name || 'Admin').split(' ')[0]}
          </text>
        </motion.g>
      ))}

      {/* Supervisor nodes */}
      {supervisors.map((s, si) => {
        const isFocused = focusedSupId === s.id;
        const dimmed = focusedSupId && !isFocused;
        const used = caseloadCounts.get(s.id) || 0;
        const supervisees = bySupervisor.get(s.id) || [];
        return (
          <motion.g key={s.id}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: dimmed ? 0.3 : 1, scale: isFocused ? 1.2 : 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20, delay: si * 0.06 }}
            style={{ cursor: 'pointer' }}
            onClick={() => setFocusedSupId(isFocused ? null : s.id)}
            onMouseEnter={() => setHoverUser(s)}
            onMouseLeave={() => setHoverUser(null)}
            data-testid={`pulse-supervisor-${s.id}`}
          >
            {/* pulse-glow ring on the supervisor node */}
            <circle cx={supXs[si]} cy={supY} r={30} fill="none" stroke="#38bdf8" strokeWidth="1" opacity="0.4">
              <animate attributeName="r" values="28;38;28" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.5;0;0.5" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle cx={supXs[si]} cy={supY} r={26} fill="#38bdf8" fillOpacity="0.28" stroke="#38bdf8" strokeWidth="1.6" filter="url(#glow)" />
            <text x={supXs[si]} y={supY + 5} textAnchor="middle" className="fill-white font-bold text-[12px]">
              {(s.full_name || 'S').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </text>
            <text x={supXs[si]} y={supY + 46} textAnchor="middle" className="fill-sky-200 text-[9px] font-semibold">
              {(s.full_name || 'Supervisor').split(' ')[0]}
            </text>
            <text x={supXs[si]} y={supY + 58} textAnchor="middle" className="fill-slate-400 text-[8px]">
              {supervisees.length} team · {used} clients
            </text>
          </motion.g>
        );
      })}

      {/* Clinician satellite nodes (arranged under their supervisor) */}
      {supervisors.map((s, si) => {
        const supervisees = bySupervisor.get(s.id) || [];
        const isDimmed = focusedSupId && focusedSupId !== s.id;
        return supervisees.map((cl, ci) => {
          const cliRow = Math.floor(ci / 4);
          const cliCol = ci % 4;
          const cliX = supXs[si] + (cliCol - 1.5) * 48;
          const cliY = 320 + cliRow * 72;
          const used = caseloadCounts.get(cl.id) || 0;
          const cap = cl.caseload_max || 12;
          return (
            <g
              key={cl.id}
              onMouseEnter={() => setHoverUser(cl)}
              onMouseLeave={() => setHoverUser(null)}
            >
              <OrbitNode
                user={cl} x={cliX} y={cliY} r={18} used={used} cap={cap}
                dimmed={isDimmed}
                onClick={() => onNodeClick(cl)}
              />
            </g>
          );
        });
      })}

      {/* Unaffiliated clinicians — bottom band, always visible */}
      {unassigned.length > 0 && (
        <>
          <line x1={40} y1={unaffBandTop - 30} x2={W - 40} y2={unaffBandTop - 30} stroke="#334155" strokeDasharray="3 4" strokeWidth="1" />
          <text x={40} y={unaffBandTop - 15} className="fill-slate-400 text-[9px] font-semibold uppercase tracking-wider">
            Unaffiliated · {unassigned.length}
          </text>
          {unassigned.map((cl, i) => {
            const col = i % unaffCols;
            const row = Math.floor(i / unaffCols);
            const cliX = 60 + col * unaffXStep;
            const cliY = unaffBandTop + 10 + row * 55;
            const used = caseloadCounts.get(cl.id) || 0;
            const cap = cl.caseload_max || 12;
            return (
              <g key={cl.id}
                onMouseEnter={() => setHoverUser(cl)}
                onMouseLeave={() => setHoverUser(null)}
              >
                <OrbitNode
                  user={cl} x={cliX} y={cliY} r={16} used={used} cap={cap}
                  onClick={() => onNodeClick(cl)}
                />
              </g>
            );
          })}
        </>
      )}
    </svg>
  );
};

// ─── Right-rail: role-adaptive action panel ────────────────────────────────
const ActionRail = ({ user, tree, unassignedClients, overCapUsers, onNavigate, onClose }) => {
  const showAssignments = rbac.isSupervisor(user) || rbac.isAdmin(user);
  const showMyTeam = user?.is_supervisor || user?.role === 'supervisor' || rbac.isAdmin(user);
  const showSupervisor = rbac.isSupervisor(user) || rbac.isAdmin(user);

  // AI Suggestion — simple heuristic: pick one over-cap user and one under-cap
  const suggestion = useMemo(() => {
    if (!overCapUsers || overCapUsers.length === 0) return null;
    const heaviest = overCapUsers[0];
    // find someone with slack
    const relief = (tree.clinicians || []).find(cl => {
      const used = tree.caseloadCounts.get(cl.id) || 0;
      const cap = cl.caseload_max || 12;
      return cap - used >= 2 && cl.id !== heaviest.id;
    });
    if (!heaviest || !relief) return null;
    return { heaviest, relief };
  }, [overCapUsers, tree]);

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Health stripe */}
      <div className="grid grid-cols-3 gap-2" data-testid="pulse-health-stripe">
        <StatCard
          label="Unassigned"
          value={unassignedClients}
          tone={unassignedClients > 0 ? 'rose' : 'emerald'}
          icon={<AlertTriangle className="w-3.5 h-3.5" />}
          testid="pulse-stat-unassigned"
          onClick={() => onNavigate('/assignments?filter=unassigned')}
        />
        <StatCard
          label="Over Cap"
          value={overCapUsers.length}
          tone={overCapUsers.length > 0 ? 'amber' : 'emerald'}
          icon={<TrendingUp className="w-3.5 h-3.5" />}
          testid="pulse-stat-overcap"
          onClick={() => onNavigate('/assignments/analytics')}
        />
        <StatCard
          label="Team Size"
          value={tree.clinicians.length + tree.supervisors.length}
          tone="sky"
          icon={<UsersIcon className="w-3.5 h-3.5" />}
          testid="pulse-stat-team"
          onClick={() => onNavigate('/my-team')}
        />
      </div>

      {/* Action pills */}
      <div className="flex flex-col gap-2 mt-1">
        {showAssignments && (
          <ActionPill
            icon={<ClipboardList className="w-4 h-4" />}
            label="Assignment Board"
            hint="Bulk assign · saved views · filters"
            gradient="from-blue-500 to-indigo-600"
            onClick={() => onNavigate('/assignments?from=pulse')}
            testid="pulse-action-assignments"
          />
        )}
        {showMyTeam && (
          <ActionPill
            icon={<UserCog className="w-4 h-4" />}
            label="My Team"
            hint={`${tree.supervisors.find(s => s.id === user?.id)?.full_name ? 'Your' : 'Team'} caseload rollup`}
            gradient="from-purple-500 to-fuchsia-600"
            onClick={() => onNavigate('/my-team?from=pulse')}
            testid="pulse-action-my-team"
          />
        )}
        {showSupervisor && (
          <ActionPill
            icon={<UsersIcon className="w-4 h-4" />}
            label="Supervisor Center"
            hint="Supervision logs · oversight"
            gradient="from-emerald-500 to-teal-600"
            onClick={() => onNavigate('/dashboard?tab=supervisor')}
            testid="pulse-action-supervisor"
          />
        )}
      </div>

      {/* AI Suggestion Card */}
      {suggestion && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-3 rounded-xl bg-gradient-to-br from-violet-500/15 via-fuchsia-500/10 to-indigo-500/15 border border-violet-400/30 backdrop-blur-sm"
          data-testid="pulse-ai-suggestion"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-semibold text-violet-200 uppercase tracking-wider mb-1.5">
            <Sparkles className="w-3 h-3" /> AI Suggestion
          </div>
          <p className="text-xs text-slate-200 leading-snug">
            <span className="font-semibold text-rose-300">{suggestion.heaviest.full_name?.split(' ')[0]}</span> is over cap
            {' '}({tree.caseloadCounts.get(suggestion.heaviest.id) || 0}/{suggestion.heaviest.caseload_max || 12}).
            {' '}
            <span className="font-semibold text-emerald-300">{suggestion.relief.full_name?.split(' ')[0]}</span> has open slots
            {' '}({suggestion.relief.caseload_max - (tree.caseloadCounts.get(suggestion.relief.id) || 0)} available).
            {' '}Rebalance?
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              onClick={() => onNavigate(`/assignments?rebalance=${suggestion.heaviest.id}:${suggestion.relief.id}`)}
              className="w-full py-1.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-xs font-semibold flex items-center justify-center gap-1 transition-colors"
              data-testid="pulse-ai-apply-btn"
            >
              <Zap className="w-3 h-3" /> Open Rebalance
            </button>
            <button
              onClick={async () => {
                const token = sessionStorage.getItem('token') || localStorage.getItem('token');
                try {
                  const r = await axios.post(`${API_URL}/api/rebalance/nudge`, {
                    heaviest_user_id: suggestion.heaviest.id,
                    relief_user_id: suggestion.relief.id,
                    reason: 'Sent from Practice Pulse AI Suggestion',
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  const d = r.data || {};
                  toast.success(
                    `Nudge sent to ${d.recipients_count || 0} supervisor${(d.recipients_count || 0) === 1 ? '' : 's'}`,
                    { description: d.emails_sent > 0 ? `${d.emails_sent} email${d.emails_sent === 1 ? '' : 's'} delivered` : 'Logged in audit trail' }
                  );
                } catch (e) {
                  toast.error('Failed to send rebalance nudge', {
                    description: e?.response?.data?.detail || e?.message || 'Try again in a moment',
                  });
                }
              }}
              className="w-full py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold flex items-center justify-center gap-1 transition-colors"
              data-testid="pulse-ai-nudge-btn"
              title="Email affected supervisors + log to audit trail"
            >
              <Send className="w-3 h-3" /> Nudge Supervisor
            </button>
          </div>
        </motion.div>
      )}

      <div className="flex items-center justify-between text-[10px] text-slate-500 pt-2 pr-3 border-t border-slate-700/40">
        <span className="flex items-center gap-1"><Activity className="w-3 h-3 animate-pulse text-emerald-400" /> Live</span>
        <kbd className="px-1.5 py-0.5 rounded bg-slate-800/60 border border-slate-700 text-slate-300">Esc</kbd>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, tone = 'sky', icon, testid, onClick }) => {
  const toneMap = {
    rose: 'from-rose-500/20 to-rose-500/5 text-rose-200 border-rose-400/30',
    amber: 'from-amber-500/20 to-amber-500/5 text-amber-200 border-amber-400/30',
    emerald: 'from-emerald-500/20 to-emerald-500/5 text-emerald-200 border-emerald-400/30',
    sky: 'from-sky-500/20 to-sky-500/5 text-sky-200 border-sky-400/30',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-2 rounded-lg bg-gradient-to-br ${toneMap[tone]} border backdrop-blur-sm text-left transition-transform hover:scale-[1.03] flex items-center justify-between gap-2`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider opacity-90 whitespace-nowrap">
          {icon}<span>{label}</span>
        </div>
      </div>
      <div className="text-xl font-bold leading-none">{value}</div>
    </button>
  );
};

const ActionPill = ({ icon, label, hint, gradient, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`group relative overflow-hidden px-4 py-3 rounded-xl bg-gradient-to-r ${gradient} text-white flex items-center justify-between transition-transform hover:scale-[1.02] hover:shadow-lg`}
  >
    <div className="flex items-center gap-2.5">
      <div className="p-1.5 rounded-lg bg-white/20">{icon}</div>
      <div className="text-left">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-[10px] opacity-80">{hint}</div>
      </div>
    </div>
    <ChevronRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" />
  </button>
);

// ─── Main container ──────────────────────────────────────────────────────
const PracticePulseMenu = ({ open, onClose, user, users = [], clients = [] }) => {
  const navigate = useNavigate();
  const [focusedSupId, setFocusedSupId] = useState(null);
  const [hoverUser, setHoverUser] = useState(null);

  const tree = useMemo(() => buildOrgTree(users, clients), [users, clients]);

  // Metrics
  const unassignedClients = useMemo(
    () => (clients || []).filter(c => c.is_active !== false && (!c.assignments || c.assignments.length === 0) && !c.assigned_therapist_id).length,
    [clients]
  );
  const activeClients = useMemo(
    () => (clients || []).filter(c => c.is_active !== false).length,
    [clients]
  );
  const overCapUsers = useMemo(
    () => (users || []).filter(u => {
      const used = tree.caseloadCounts.get(u.id) || 0;
      const cap = u.caseload_max || 0;
      return cap > 0 && used >= cap;
    }).sort((a, b) => (tree.caseloadCounts.get(b.id) || 0) - (tree.caseloadCounts.get(a.id) || 0)),
    [users, tree]
  );
  // Practice-health rollup — how much of the workforce is running healthy
  const healthBreakdown = useMemo(() => {
    let healthy = 0, near = 0, over = 0, uncapped = 0;
    (users || []).forEach(u => {
      if (u.role === 'admin' || u.role === 'super_admin') return;
      const used = tree.caseloadCounts.get(u.id) || 0;
      const cap = u.caseload_max || 0;
      if (!cap) { uncapped += 1; return; }
      const pct = used / cap;
      if (pct >= 1) over += 1;
      else if (pct >= 0.8) near += 1;
      else healthy += 1;
    });
    return { healthy, near, over, uncapped };
  }, [users, tree]);
  // Average caseload across capped users
  const avgCaseload = useMemo(() => {
    const usersWithCap = (users || []).filter(u => u.caseload_max);
    if (!usersWithCap.length) return 0;
    const total = usersWithCap.reduce((sum, u) => sum + (tree.caseloadCounts.get(u.id) || 0), 0);
    return (total / usersWithCap.length).toFixed(1);
  }, [users, tree]);

  // All internal Pulse navigations flow through this — automatically stamps
  // `from=pulse` on any path other than `/dashboard*` so downstream pages
  // (Assignment Board, My Team, Analytics, etc.) can route their Back button
  // to /dashboard?pulse=1 and re-open the panel where the user left off.
  const doNav = useCallback((path) => {
    let final = path;
    if (!path.startsWith('/dashboard') && !path.includes('from=pulse')) {
      final = path + (path.includes('?') ? '&' : '?') + 'from=pulse';
    }
    navigate(final);
    onClose();
  }, [navigate, onClose]);

  const handleNodeClick = useCallback((clinician) => {
    doNav(`/assignments?filter=staff:${clinician.id}`);
  }, [doNav]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === '1') doNav('/assignments');
      if (e.key === '2') doNav('/my-team');
      if (e.key === '3') doNav('/dashboard?tab=supervisor');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, doNav]);

  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm z-[60]"
            onClick={onClose}
            data-testid="pulse-backdrop"
          />
          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            className="fixed inset-x-0 mx-auto top-16 md:top-20 w-[92vw] max-w-[1180px] max-h-[86vh] z-[61] rounded-2xl overflow-hidden shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 60%, rgba(15,23,42,0.98) 100%)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              backdropFilter: 'blur(24px)',
            }}
            data-testid="practice-pulse-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/40">
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <div className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-400 animate-ping opacity-60" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white tracking-wide">Practice Pulse</h2>
                  <p className="text-[10px] text-slate-400">
                    {tree.admins.length} admin · {tree.supervisors.length} supervisor{tree.supervisors.length === 1 ? '' : 's'} · {tree.clinicians.length} clinician{tree.clinicians.length === 1 ? '' : 's'} · {activeClients} active clients
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Practice health mini-summary — always visible in header */}
                <div className="hidden md:flex items-center gap-2 text-[10px] px-2.5 py-1 rounded-full border border-slate-700/60 bg-slate-900/40" data-testid="pulse-health-summary">
                  <span className="flex items-center gap-1 text-emerald-300"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />{healthBreakdown.healthy}</span>
                  <span className="flex items-center gap-1 text-amber-300"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />{healthBreakdown.near}</span>
                  <span className="flex items-center gap-1 text-rose-300"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" />{healthBreakdown.over}</span>
                  <span className="text-slate-500 ml-1">·</span>
                  <span className="text-slate-400">avg {avgCaseload}</span>
                </div>
                {focusedSupId && (
                  <button
                    onClick={() => setFocusedSupId(null)}
                    className="text-[10px] text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700/60"
                    data-testid="pulse-reset-focus"
                  >
                    Reset focus
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-slate-800/70 text-slate-400 hover:text-white transition-colors"
                  data-testid="pulse-close"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body — grid */}
            <div className="grid grid-cols-1 lg:grid-cols-[58%,42%] max-h-[calc(86vh-56px)]">
              {/* Org graph */}
              <div className="relative p-3 overflow-hidden border-r border-slate-700/40 min-h-[440px]">
                <OrgGraph
                  tree={tree}
                  focusedSupId={focusedSupId}
                  setFocusedSupId={setFocusedSupId}
                  onNodeClick={handleNodeClick}
                  hoverUser={hoverUser}
                  setHoverUser={setHoverUser}
                />
                {/* Hover tooltip — enriched with role, caseload, cap %, and top-of-tree info */}
                <AnimatePresence>
                  {hoverUser && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="absolute top-3 right-3 px-3 py-2 rounded-lg bg-slate-900/95 border border-slate-700 shadow-xl min-w-[210px]"
                      data-testid="pulse-hover-tooltip"
                    >
                      <div className="text-xs font-semibold text-white">{hoverUser.full_name || hoverUser.email}</div>
                      <div className="text-[10px] text-slate-400 capitalize">
                        {hoverUser.role?.replace(/_/g, ' ')}{hoverUser.is_supervisor ? ' · supervisor' : ''}
                      </div>
                      {(() => {
                        const used = tree.caseloadCounts.get(hoverUser.id) || 0;
                        const cap = hoverUser.caseload_max || 0;
                        const pct = cap ? Math.round((used / cap) * 100) : null;
                        return (
                          <div className="mt-1.5 space-y-0.5 text-[10px]">
                            <div className="flex justify-between text-slate-300">
                              <span>Clients</span>
                              <span className="font-semibold text-sky-300">{used}{cap ? ` / ${cap}` : ''}</span>
                            </div>
                            {pct !== null && (
                              <div className="flex justify-between text-slate-300">
                                <span>Utilization</span>
                                <span className={`font-semibold ${pct >= 100 ? 'text-rose-300' : pct >= 80 ? 'text-amber-300' : 'text-emerald-300'}`}>{pct}%</span>
                              </div>
                            )}
                            {hoverUser.email && (
                              <div className="text-slate-500 truncate mt-1" title={hoverUser.email}>{hoverUser.email}</div>
                            )}
                            <div className="mt-1 pt-1 border-t border-slate-800 text-slate-500 text-[9px] italic">
                              Click to filter Assignment Board →
                            </div>
                          </div>
                        );
                      })()}
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Legend */}
                <div className="absolute bottom-2 left-3 flex items-center gap-3 text-[9px] text-slate-400">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> healthy</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> near cap</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400" /> over cap</span>
                  <span className="ml-2 opacity-60">click a supervisor to focus</span>
                </div>
              </div>

              {/* Right rail */}
              <div className="overflow-y-auto max-h-[calc(86vh-56px)] pr-1">
                <ActionRail
                  user={user}
                  tree={tree}
                  unassignedClients={unassignedClients}
                  overCapUsers={overCapUsers}
                  onNavigate={doNav}
                  onClose={onClose}
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PracticePulseMenu;
